import { Typography } from "@mui/material";
import { useDeleteMerchantTerminalContractMutation } from "Api/Mutations/Merchants/useDeleteMerchantTerminalContractMutation";
import { useDownloadTerminalContract } from "Api/Mutations/Merchants/useDownloadTerminalContract";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import type { CardListItemType } from "Components/Shared/CardsList/CardsList";
import { ControlledCardsList } from "Components/Shared/CardsList/ControlledCardsList";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { EditContractDialogContent } from "Components/Shared/Merchant/Products/TerminalContracts/EditContractDialogContent";
import { ViewContractDialogContent } from "Components/Shared/Merchant/Products/TerminalContracts/ViewContractDialogContent";
import type { MerchantTerminalContractDto } from "Infrastructure/Api/Api";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const COMPONENT_RESOURCES = Resources.Components.Merchant.TerminalContracts;

type Props = {
  isReadOnly?: boolean;
  merchantPublicID: string;
  contracts: Array<MerchantTerminalContractDto>;
};

export const TerminalContracts = ({
  isReadOnly,
  merchantPublicID,
  contracts,
}: Props) => {
  const [selectedContract, setSelectedContract] =
    useState<MerchantTerminalContractDto | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);

  const { t } = useResource();
  const { data: merchantProducts, isLoading: isMerchantProductsLoading } =
    useGetMerchantAppProductsQuery(merchantPublicID);
  const { mutate: downloadTerminalContract, isPending: isDownloadPending } =
    useDownloadTerminalContract();
  const { mutate: deleteContract, isPending: isDeletePending } =
    useDeleteMerchantTerminalContractMutation({
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.DeleteSuccess));
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.DeleteError));
      },
    });

  if (isMerchantProductsLoading) {
    return <CenteredCircularProgress />;
  }

  const listItems = contracts
    .map((contract) => {
      const { type, merchantPublicID, publicID } = contract;

      return {
        id: publicID,
        title: (
          <Typography fontWeight="500" fontSize="1rem">
            {t(COMPONENT_RESOURCES.ProductType[type])}
          </Typography>
        ),
        isDisabled: isDeletePending || isDownloadPending,
        onDownload: () => {
          downloadTerminalContract({
            publicID: merchantPublicID,
            terminalContractPublicID: publicID,
          });
        },
        ...(isReadOnly
          ? {
              onView: () => {
                setSelectedContract(contract);
                setIsViewOpen(true);
              },
            }
          : {
              onEdit: () => {
                setSelectedContract(contract);
                setIsUpdateOpen(true);
              },
              onDelete: () => {
                confirm(
                  t(COMPONENT_RESOURCES.DeleteConfirmation, {
                    type,
                  }),
                ) &&
                  deleteContract({
                    merchantPublicID,
                    terminalContractPublicID: publicID,
                  });
              },
            }),
      };
    })
    .filter((item) => !!item) as Array<CardListItemType>; // ts build throws error without the explicit type casting;

  const hasProductWithTerminalContractType = merchantProducts?.some(
    ({ productDto }) => !!productDto.terminalContractType,
  );

  return (
    <>
      <ControlledCardsList
        listItems={listItems}
        isReadOnly={isReadOnly || !hasProductWithTerminalContractType}
        addButtonConfig={{
          onClick: () => {
            setSelectedContract(null);
            setIsUpdateOpen(true);
          },
          text: t(COMPONENT_RESOURCES.AddButtonText),
        }}
        noItemsText={t(COMPONENT_RESOURCES.NoTerminalContracts)}
      />
      <ModalDialog
        isOpen={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        title={
          selectedContract
            ? t(COMPONENT_RESOURCES.EditContractDialogTitle)
            : t(COMPONENT_RESOURCES.NewContractDialogTitle)
        }
      >
        <EditContractDialogContent
          isOpen={isUpdateOpen}
          contract={selectedContract}
          merchantPublicID={merchantPublicID}
          terminalContractPublicID={selectedContract?.publicID || ""}
          onAfterSubmit={(isSuccess) => {
            if (isSuccess) {
              setSelectedContract(null);
              setIsUpdateOpen(false);
            }
          }}
        />
      </ModalDialog>
      <ModalDialog
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={t(COMPONENT_RESOURCES.ViewContractDialogTitle)}
      >
        <ViewContractDialogContent
          isOpen={isViewOpen}
          merchantPublicID={merchantPublicID}
          terminalContractPublicID={selectedContract?.publicID || ""}
        />
      </ModalDialog>
    </>
  );
};
